import { ReactElement, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';
import { AlertRule, ActionData, ParametersType } from '../constants/telosair-interfaces';
import Button from '../controls/Button';
import SidePanelContainer from '../controls/SidePanelContainer';
import { createAlertRule, updateAlertRule } from '../services/dashboard-service';
import { useData } from '../context/data.context';
import { PRIORITY_LIST, RHFPatterns, TOAST_MESSAGES } from '../constants/global-constants';
import MultiSelectDropdown from '../controls/MultiSelectDropDown';
import CustomDropdown, { CustomDropdownOption } from '../controls/CustomDropdown';
import CustomInputBox from '../controls/CustomInputBox';

type FORMDATA = {
  priority: string;
  devices: {
    label: string;
    value: string;
  }[];
  parameter: string;
  days: {
    label: string;
    value: string;
  }[];
  fromValue: string;
  toValue: string;
  timeUnit: string;
  timeValue: string;
  startDate: string;
  endTime: string;
  notification: string;
  mobileNumber: string;
};

const priorityList = [
  { value: 'Critical', label: 'Critical' },
  { value: 'High', label: 'High' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Low', label: 'Low' }
];

const daysList = [
  { value: '-1', label: 'All' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '7', label: 'Sunday' }
];

const aggWindow = ['Hours', 'Minutes', 'Seconds'];

type CreateAlertRuleModalProps = {
  handleClose: (refresh?: boolean) => void;
  actionData?: ActionData<AlertRule>;
};

/**
 * Modal - AddDeviceModal.
 * @param props - Properties of AddDeviceModal.
 * @returns ReactElement.
 */
export default function CreateAlertRuleModal(props: CreateAlertRuleModalProps): ReactElement {
  const { actionData, handleClose } = props;
  const { devices: deviceList, parameters } = useData();
  // const [devices, setDevices] = useState<ExtendedDeviceAttributes[]>([]);
  const methods = useForm<FORMDATA>({
    mode: 'all',
    defaultValues: {
      timeValue: '1',
      timeUnit: 'Minutes',
      startDate: '00:00',
      endTime: '23:59',
      days: [{ value: '-1', label: 'All' }]
    }
  });
  const { handleSubmit, reset, watch, control } = methods;
  const [loading, setLoading] = useState(false);
  const parameterList: CustomDropdownOption[] = [
    ...parameters,
    {
      displayName: 'Sensor status',
      parameterName: 'sensor_status' as ParametersType
    }
  ].map((item) => ({ label: item.displayName, value: item.parameterName }));

  useEffect(() => {
    if (actionData?.type === 'edit') {
      const devices = [
        { label: 'All', value: '0' },
        ...deviceList.map((device) => ({
          label: device.serialNumber.toString(),
          value: device.serialNumber.toString()
        }))
      ];
      const selectedDays = actionData.data.dayOfWeek?.split(',');
      const days = daysList.filter((item) => selectedDays?.includes(item.value));
      const selectedDevices = actionData.data.deviceId?.split(',');
      const selectedDevicesArr = devices.filter((item) => selectedDevices?.includes(item.value));
      reset({
        ...actionData.data,
        parameter: actionData.data.parameterName,
        days,
        priority: actionData.data.priority,
        devices: selectedDevicesArr,
        timeUnit: actionData.data.timeUnit
      });
    }
  }, [actionData, deviceList, parameters]);

  /**
   * Handles create alert rule.
   @param formData - Form data.
   */
  async function handleCreateAlertRule(formData: FORMDATA): Promise<void> {
    const days = formData.days?.map((item: any) => item.value);
    const devices = formData.devices?.map((item: any) => parseInt(item.value, 10));
    const body = {
      id: actionData?.type === 'edit' ? actionData?.data?.id : 0,
      alertName: '',
      alertDescription: '',
      priority: formData.priority,
      deviceId: devices?.join(','),
      parameterName: formData.parameter,
      aggType: 'avg',
      timeUnit: formData.timeUnit,
      timeValue: formData.timeValue,
      // filter: null,
      notification: formData.notification,
      mobileNumber: formData.mobileNumber,
      groupBy: 'serial_number',
      parameterValue: '',
      clientName: sessionStorage.getItem('client_name'),
      operator: '',
      // status: 'Active',
      reportDataMissing: false,
      alertType: 'threshold',
      fromValue: formData.fromValue,
      toValue: formData.toValue,
      // alertState: null,
      // deviceId: null,
      startDate: formData.startDate,
      endTime: formData.endTime,
      dayOfWeek: days?.join(','),
      days,
      createLogEntry: true
    };
    try {
      setLoading(true);
      if (actionData?.type === 'edit') {
        await updateAlertRule(body);
        toast.success(TOAST_MESSAGES.edited);
      } else {
        await createAlertRule(body);
        toast.success(TOAST_MESSAGES.created);
      }
      handleClose(true);
      setLoading(false);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      setLoading(false);
    }
  }
  const priorityValue = watch('priority');
  const isCritical = priorityValue === PRIORITY_LIST.critical;
  const isHigh = priorityValue === PRIORITY_LIST.high;
  // const isMedium = priorityValue === PRIORITY_LIST.medium;
  const isLow = priorityValue === PRIORITY_LIST.low;
  return (
    <SidePanelContainer closeModal={() => handleClose()}>
      <div className="pt-3 flex text-gray-500 dark:text-gray-300 text-lg">
        {actionData?.type === 'edit' ? 'Edit Rule' : 'Create Rule'}
      </div>
      <div className="pb-3 flex text-gray-500 dark:text-gray-500 text-sm">
        Create customised alerts
      </div>
      {/* eslint-disable-next-line */}
      <FormProvider {...methods}>
        <form noValidate action="post" onSubmit={handleSubmit(handleCreateAlertRule)}>
          <CustomDropdown
            validationObject={{ required: 'Please select a priority' }}
            id="priority"
            label="Priority"
            options={priorityList}
          />
          <MultiSelectDropdown
            id="devices"
            label="Devices"
            validationObject={{ required: 'Please select atleast one value' }}
            control={control}
            options={[
              { label: 'All', value: '0' },
              ...deviceList.map((device) => ({
                label: device.deviceName || device.serialNumber.toString(),
                value: device.serialNumber.toString()
              }))
            ]}
          />
          <CustomDropdown label="Parameter" id="parameter" options={parameterList} />
          <MultiSelectDropdown id="days" label="Days" options={daysList} />
          <div className="flex gap-4">
            <CustomInputBox
              validationObject={{ required: 'Please enter lower threshold value' }}
              label="Lower Threshold"
              idname="fromValue"
              type="number"
            />
            <CustomInputBox
              validationObject={{ required: 'Please enter higher threshold value' }}
              idname="toValue"
              label="Higher Threshold"
              type="number"
            />
          </div>
          <div className="flex gap-4">
            <CustomDropdown
              validationObject={{ required: 'Please select a aggregation window' }}
              id="timeUnit"
              label="Aggregation Window"
              options={aggWindow.map((unit) => ({ value: unit, label: unit }))}
            />
            <CustomInputBox
              validationObject={{ required: 'Please enter the time window' }}
              label="Time Window"
              idname="timeValue"
              type="number"
            />
          </div>
          <div className="flex gap-4">
            <CustomInputBox label="Start time" idname="startDate" type="time" />
            <CustomInputBox label="End time" idname="endTime" type="time" />
          </div>
          {priorityValue && !isLow && (
            <CustomInputBox
              validationObject={{
                required: isHigh ? 'Please enter notification email' : false,
                pattern: RHFPatterns.email
              }}
              label="Notification Email"
              idname="notification"
              autoComplete="email"
              type="email"
            />
          )}
          {(isCritical || isHigh) && (
            <CustomInputBox
              autoComplete="tel"
              validationObject={{
                required: isCritical ? 'Please enter notification mobile number' : false,
                maxLength: {
                  value: 12,
                  message: 'Please enter a valid mobile number'
                }
              }}
              label="Notification Mobile Number"
              idname="mobileNumber"
              type="number"
            />
          )}
          <div
            style={{ width: 'inherit' }}
            className="p-5 fixed bottom-0 right-0 flex justify-center gap-3 bg-white dark:bg-container-600"
          >
            <div className="">
              <Button
                theme="secondary-color"
                size="sm"
                name="Discard"
                onClick={() => {
                  handleClose();
                }}
                isBlockButton
              />
            </div>
            <Button
              theme="primary"
              size="sm"
              name="Save Alert"
              loading={loading}
              buttonType="submit"
              isBlockButton
            />
          </div>
        </form>
      </FormProvider>
    </SidePanelContainer>
  );
}
